
import {
  computed, defineComponent, ref, onBeforeMount, watch,
} from 'vue';

import Tooltip from 'primevue/tooltip';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';

import Flag from '@/components/table/renderers/Flag.vue';
import LanguageIcons from '@/components/table/renderers/LanguageIcons.vue';
import List from '@/components/data-explorer/fields/List.vue';
import AddressItem from '@/components/data-explorer/fields/AddressItem.vue';
import OrderItem from '@/components/data-explorer/fields/OrderItem.vue';
import CartItem from '@/components/data-explorer/fields/CartItem.vue';
import ContactSourceProfiles from '@/components/data-explorer/fields/ContactSourceProfiles.vue';

import {
  getContactAddressesNest,
  getContactCustomDefinitionsNest,
  getContactGroupsNest,
  getContactListNest,
  getContactOrdersNest,
  getContactCartsNest,
} from '@/composables/data-explorer/DataExplorer';

import { fetchDataSources } from '@/composables/segmentations/helpers/fetchData';
import getChannelSubscriptionStatus, {
  ChannelSubscriptionStatus,
  SubscriptionChannel,
} from '@/composables/contacts/channelSubscription';

import { useStore } from '@/store';

import { formatDate } from '@/helpers/Date';

import {
  ContactAddress,
  ContactCustomDefinition,
  ContactGroup,
  ContactReject,
  Order,
  Cart,
} from '@/types/data-explorer-types';

import { useI18n } from 'vue-i18n';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import GdprEraseAction from '@/views/mes-listes/customers/ListsContactsActions/GdprEraseAction.vue';
import type { GdprEraseResult } from '@/views/mes-listes/customers/ListsContactsActions/types';

/** Dialogue d'effacement RGPD, piloté par sa méthode `open` exposée (SC-3851). */
interface GdprEraseDialog {
  open?: (payload: { context: string; selectionInfo: null; rowId: null }) => void;
}

export default defineComponent({
  name: 'ContactFile',

  directives: {
    tooltip: Tooltip,
  },

  components: {
    AddressItem,
    TabView,
    TabPanel,
    Flag,
    LanguageIcons,
    List,
    OrderItem,
    CartItem,
    ContactSourceProfiles,
    SpmBadge,
    SpmButton,
    GdprEraseAction,
  },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();

    const selectedData = computed(() => store.getters['dataExplorer/getSelectedData']);

    // Nombre de fiches client affichées dans « Points de vente et identifiants »,
    // remis à zéro quand la fiche passe à un autre contact.
    const sourceProfilesCount = ref(0);
    watch(() => selectedData.value?.generalInformation?.data?.idShopContact, () => {
      sourceProfilesCount.value = 0;
    });

    // Data sources lookup
    const sourceLabelsMap = ref<Map<number, string>>(new Map());

    onBeforeMount(async () => {
      const sources = await fetchDataSources();
      const map = new Map<number, string>();
      sources.forEach((s: { label: string; value: number }) => {
        map.set(s.value, s.label);
      });
      sourceLabelsMap.value = map;
    });

    const getSourceLabel = (dataSourceId: number | string | null | undefined): string => {
      if (!dataSourceId) return '';
      return sourceLabelsMap.value.get(Number(dataSourceId)) || '';
    };

    // Contact sources: collect unique sources from orders and carts
    const contactSources = computed(() => {
      const sourceIds = new Set<number>();
      if (selectedData.value?.orders?.data) {
        selectedData.value.orders.data.forEach((o: any) => {
          if (o.dataSourceId) sourceIds.add(Number(o.dataSourceId));
        });
      }
      if (selectedData.value?.carts?.data) {
        selectedData.value.carts.data.forEach((c: any) => {
          if (c.dataSourceId) sourceIds.add(Number(c.dataSourceId));
        });
      }
      return Array.from(sourceIds)
        .map((id) => ({ id, label: sourceLabelsMap.value.get(id) || `#${id}` }))
        .filter((s) => s.label);
    });

    const hasName = computed(() => {
      const first = selectedData.value?.generalInformation?.data?.firstName?.trim();
      const last = selectedData.value?.generalInformation?.data?.lastName?.trim();
      return !!(first || last);
    });

    const initials = computed(() => {
      const first = selectedData.value?.generalInformation?.data?.firstName?.[0] || '';
      const last = selectedData.value?.generalInformation?.data?.lastName?.[0] || '';
      return (first + last).toUpperCase() || '?';
    });

    const gender = computed(() => {
      if (selectedData.value && selectedData.value.generalInformation.data.gender && selectedData.value.generalInformation.data.gender.toString() === '1') {
        return {
          label: t('dataExplorer.contactFile.fields.gender.male'),
          icon: 'fa fa-mars',
        };
      } if (selectedData.value && selectedData.value.generalInformation.data.gender && selectedData.value.generalInformation.data.gender.toString() === '2') {
        return {
          label: t('dataExplorer.contactFile.fields.gender.female'),
          icon: 'fa fa-venus',
        };
      }
      return {
        label: t('dataExplorer.contactFile.fields.gender.unknown'),
        icon: 'fa fa-neuter',
      };
    });
    const hasAccount = computed(() => selectedData.value
      && (selectedData.value.generalInformation.data.type && selectedData.value.generalInformation.data.type & 1) === 1);
    const isAccountActive = computed(() => selectedData.value
      && selectedData.value.generalInformation.data.active && selectedData.value.generalInformation.data.active.toString() === '1');

    // ===== RGPD (SC-3851) =====

    const gdprEraseRef = ref<GdprEraseDialog | null>(null);

    // RGPD (SC-3851) : effacement définitif, disponible pour tous les profils —
    // aligné sur la liste des contacts. Les garde-fous sont ailleurs : un seul
    // contact à la fois, confirmation par mot tapé, et côté serveur refus des
    // clés API et plafonds de cadence.
    const canGdprErase = computed(() => true);

    /**
     * Le compte client a été supprimé sur la boutique : le contact est désactivé
     * chez nous, mais ses données restent (statistiques, commandes). C'est très
     * exactement le point qui a motivé le ticket SC-3851.
     */
    const isAccountDeletedOnShop = computed(() => hasAccount.value && !isAccountActive.value);

    const openGdprErase = () => {
      if (gdprEraseRef.value?.open) {
        gdprEraseRef.value.open({ context: 'row', selectionInfo: null, rowId: null });
      }
    };

    const onGdprEraseConfirm = async (result: GdprEraseResult) => {
      const idShopContact = selectedData.value?.generalInformation?.data?.idShopContact;
      if (!idShopContact) return;

      try {
        const response: { success?: boolean; message?: string } = await nestCall(
          'post',
          NestApiType.CLIENT_API,
          '/list-actions',
          {},
          '',
          {
            action_type: 'gdpr_erase',
            selection_type: 'manual',
            selected_ids: [String(idShopContact)],
            source_list_id: 'my_contacts',
            confirmation: result.confirmation,
          },
          true,
        );

        if (response?.success) {
          showToastSuccess(t('dataExplorer.contactFile.gdprErase.success'));
          // Les données du contact viennent d'être effacées : la fiche ouverte
          // n'a plus rien à montrer, on la referme.
          store.dispatch('dataExplorer/closeData', { dataId: selectedData.value.dataId });
        } else {
          showToastError(t('dataExplorer.contactFile.gdprErase.error'));
        }
      } catch (error) {
        showToastError(t('dataExplorer.contactFile.gdprErase.error'));
      }
    };

    const phoneNumber = computed(() => {
      if (selectedData.value && selectedData.value.generalInformation.data.country && selectedData.value.generalInformation.data.mobile) {
        const phoneNumberObj = parsePhoneNumberFromString(
          selectedData.value.generalInformation.data.mobile,
          selectedData.value.generalInformation.data.country.toUpperCase(),
        );
        if (phoneNumberObj && phoneNumberObj.isValid()) {
          return phoneNumberObj.formatInternational();
        }
        if (selectedData.value.generalInformation.data.mobile) {
          return selectedData.value.generalInformation.data.mobile;
        }
      }
      return '-';
    });

    const isNumber = (val: string) => {
      if (!val) {
        return false;
      }
      return /^\d+(\.\d+)?$/.test(val);
    };

    const handleRetrieveAddresses = (addresses: Array<ContactAddress>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: addresses,
          dataKey: 'addresses',
        },
      );
    };

    const handleRetrieveGroups = (groups: Array<ContactGroup>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: groups,
          dataKey: 'groups',
        },
      );
    };

    const handleRetrieveRejects = (rejects: Array<ContactReject>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: rejects,
          dataKey: 'rejects',
        },
      );
    };

    const handleRetrieveShops = (shops: Array<string>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: shops,
          dataKey: 'shops',
        },
      );
    };

    const handleRetrieveOrders = (orders: Array<Order>, total: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: orders,
          total,
          dataKey: 'orders',
        },
      );
    };

    const handleOrdersNextPage = (currentPage: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          currentPage,
          dataKey: 'orders',
        },
      );
    };

    const handleRetrieveCarts = (carts: Array<Cart>, total: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: carts,
          total,
          dataKey: 'carts',
        },
      );
    };

    const handleRetrieveCustomDefinitions = (customDefinitions: Array<ContactCustomDefinition>) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: customDefinitions,
          dataKey: 'customData',
        },
      );
    };

    const openCustomDefinitionDetails = (customDefinition: ContactCustomDefinition) => {
      const idShopContact = selectedData.value?.generalInformation?.data?.idShopContact;
      if (!idShopContact) {
        return;
      }

      store.dispatch('dataExplorer/openCustomDataFile', {
        customDefinition,
        idShopContact,
      });
    };

    const handleCartsNextPage = (currentPage: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          currentPage,
          dataKey: 'carts',
        },
      );
    };

    const channelStatuses = computed(() => {
      const data = selectedData.value?.generalInformation?.data || {};
      const presentation: Record<ChannelSubscriptionStatus, { label: string; cssClass: string }> = {
        no_coordinate: { label: t('dataExplorer.contactFile.fields.notSpecified'), cssClass: 'none' },
        unsubscribed: { label: t('dataExplorer.contactFile.fields.newsletter.unsubscribe.value'), cssClass: 'unsubscribed' },
        subscribed: { label: t('dataExplorer.contactFile.fields.newsletter.optin.value'), cssClass: 'subscribed' },
        double_optin: { label: t('dataExplorer.contactFile.fields.newsletter.doubleOptin.value'), cssClass: 'double-optin' },
        simple_optin: { label: t('dataExplorer.contactFile.fields.newsletter.optin.value'), cssClass: 'optin' },
        not_subscribed: { label: t('dataExplorer.contactFile.fields.newsletter.nonOptin.value'), cssClass: 'none' },
      };
      const build = (channel: SubscriptionChannel, hasCoordinate: boolean) => presentation[
        getChannelSubscriptionStatus(channel, hasCoordinate, data.optinData?.[channel], data.unsubData?.[channel])
      ];
      return {
        email: build('email', !!data.email),
        sms: build('sms', !!data.mobile),
        push: build('push', !!data.hasPushToken),
      };
    });

    return {
      t,
      locale,
      selectedData,
      contactSources,
      sourceProfilesCount,
      getSourceLabel,
      hasName,
      initials,
      gender,
      hasAccount,
      isAccountActive,
      phoneNumber,
      channelStatuses,

      gdprEraseRef,
      canGdprErase,
      isAccountDeletedOnShop,
      openGdprErase,
      onGdprEraseConfirm,

      isNumber,
      formatDate,
      getContactAddressesNest,
      getContactCustomDefinitionsNest,
      getContactGroupsNest,
      getContactOrdersNest,
      getContactListNest,
      getContactCartsNest,
      handleRetrieveAddresses,
      handleRetrieveGroups,
      handleRetrieveShops,
      handleRetrieveOrders,
      handleOrdersNextPage,
      handleRetrieveCarts,
      handleCartsNextPage,
      handleRetrieveCustomDefinitions,
      openCustomDefinitionDetails,
    };
  },
});
